import { Area, AreaChart, CartesianGrid, Line, LineChart as RechartsLineChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip } from '../tailgrids/core/chart.js';
import { type ChartTone, SERIES_TONES, TONE_COLOR } from './chart-colors.js';
import { ChartTooltipCard, type TooltipSeries } from './ChartTooltipCard.js';

export interface LineSeries {
  key: string;
  label: string;
  tone?: ChartTone;
}

type Row = Record<string, string | number | boolean | null | undefined>;

interface LineChartProps<T extends Row> {
  data: readonly T[];
  xKey: keyof T & string;
  series: readonly LineSeries[];
  /** Accessible name; the chart is announced as "<title>: <summary>". */
  title: string;
  summary?: string;
  height?: number;
  /** Fill under each line (equity, volume over time). */
  area?: boolean;
  formatValue?: (value: number) => string;
  /** The y-axis labels when they need a shorter form than the tooltip's (`954MB`); `formatValue` otherwise. */
  formatAxis?: (value: number) => string;
  formatX?: (value: string) => string;
}

/** Lines (or filled areas) over time on the NextAdmin chart container (Recharts). */
export function LineChart<T extends Row>({
  data,
  xKey,
  series,
  title,
  summary,
  height = 260,
  area = false,
  formatValue,
  formatAxis,
  formatX,
}: LineChartProps<T>) {
  const toned: TooltipSeries[] = series.map((s, i) => ({
    ...s,
    tone: s.tone ?? SERIES_TONES[i % SERIES_TONES.length],
  }));
  const axisFormat = formatAxis ?? formatValue;
  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey={(row: T) => row[xKey]}
        axisLine={false}
        tickLine={false}
        dy={8}
        tickFormatter={formatX}
        minTickGap={24}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        width={56}
        tickFormatter={axisFormat ? (value: number) => axisFormat(value) : undefined}
      />
      <ChartTooltip
        content={(props) => (
          <ChartTooltipCard {...props} series={toned} formatLabel={formatX} formatValue={formatValue} />
        )}
      />
    </>
  );

  return (
    <figure role="img" aria-label={summary ? `${title}: ${summary}` : title} className="m-0 w-full" style={{ height }}>
      <ChartContainer className="h-full w-full" width="100%" height={height}>
        {area ? (
          <AreaChart data={[...data]} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            {axes}
            {toned.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={TONE_COLOR[s.tone]}
                fill={TONE_COLOR[s.tone]}
                fillOpacity={0.12}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : (
          <RechartsLineChart data={[...data]} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            {axes}
            {toned.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={TONE_COLOR[s.tone]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </RechartsLineChart>
        )}
      </ChartContainer>
    </figure>
  );
}
